import { IDocumentClient } from "@multiweb/clients/documents";
import { IRowData } from "@multiweb/types/contracts";
import { RequestContext } from "OW.Remoting/Remoting/RequestContext";
import { FrameContext } from "UI/UI/UI/UIManagerInterfaces";
import { convertContextFromFrameToRequest } from "UI/UI/Utilities/SharedUIMethods";
import { IDataSource, IDataSourceLoadInfo, IDataSourceLoadInfoBase, IOrderBy, IRecordsInfo } from "../../../Global/Base/DataSources/Interfaces";
import { Event } from "../../../Global/Base/Events";
import { NotImplementedException } from "../../../Global/Base/Exceptions";
import { IDisposable } from "../../../Global/Base/IDisposable";
import { CancelError, CancellationToken, CancellationTokenSource } from "../../../Global/Base/Promises/CancellationTokenSource";
import { IActualObjectChangeEventArgs } from "./EditableDBDataSource";

/**
* Zdroj dat, který vrací existují verze dokumentu získané z DMS a konvertované do obecného formátu IRowData[] pro použití v seznamech
*/
export class DocumentVersionsDataSource implements IDataSource, IDisposable {
    //#region Events
    /**
     * Vraci startindex pro cteni dat
     */
    public dataLoaded: Event<IDataSource, IDataSourceLoadInfo> = new Event<IDataSource, IDataSourceLoadInfo>();

    public error: Event<DocumentVersionsDataSource, any> = new Event<DocumentVersionsDataSource, any>();

    /**
     * Pokud je zrušeno načítání dat
     */
    public loadingCancelled: Event<DocumentVersionsDataSource, IDataSourceLoadInfoBase> = new Event<DocumentVersionsDataSource, IDataSourceLoadInfoBase>();

    /**
     * Pokud je spuštěno načítání dat
     */
    public loadingStarted: Event<DocumentVersionsDataSource, any> = new Event<DocumentVersionsDataSource, any>();

    /**
     * Pokud je ukončeno načítání dat
     */
    public loadingEnded: Event<DocumentVersionsDataSource, IDataSourceLoadInfoBase> = new Event<DocumentVersionsDataSource, IDataSourceLoadInfoBase>();

    /**
    * Vraci radek a zmeneny atribut
    */
    public readonly attributesChanged: Event<DocumentVersionsDataSource, IActualObjectChangeEventArgs> = new Event<DocumentVersionsDataSource, IActualObjectChangeEventArgs>();
    //#endregion Events

    //#region Fields
    private _instanceId: string;
    private _attributeName: string;
    protected _runningDataLoad: Promise<any>;
    protected _runningDataLoadCTS: CancellationTokenSource;
    protected _internalRows: IRowData[];
    //#endregion Fields

    //#region Properties
    public dataKey: string = "ID";
    public className: string;
    public orderBy: IOrderBy[];
    public condition: string;
    public conditionParams: any[];
    private readonly _documentClient: IDocumentClient;
    protected _frameContext: FrameContext | undefined = undefined;

    /**
    * Vrací podmínku potřebnou pro načtení dat
    */
    public getOriginalCondition(): string {
        throw new NotImplementedException();
    }

    /**
     * Vrací true, pokud je datasource filtrován.
     */
    public get hasFilterCondition(): boolean {
        return false;
    }

    //#endregion Properties

    //#region Constructors
    constructor(instanceId: string, attrName: string, documentClient: IDocumentClient, frameContext: FrameContext) {
        this._instanceId = instanceId;
        this._attributeName = attrName;
        this._documentClient = documentClient;
        this._frameContext = frameContext;
    }
    //#endregion Constructors

    //#region Methods
    //TODO: 33753
    protected _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this._frameContext, cancellationToken);
    }
    /** Vrací seznam atributů */
    public getAttrs(): string[] {
        return ["Name", "VersionDocID", "ArchiveVersionName", "ArchiveVersionDocID", "CreateTime", "Author", "Number"];
    }

    public getFullLengthAttrs(): string[] {
        return [];
    }

    /**
     * Nastavuje atributy ke čtení - není podporováno
     * @param _attrs
     */
    public setAttrs(_attrs: string[]): void {
        // Nepodporuje
    }

    /**
     * Nastavuje filtrovací podmínku - není podporováno
     * @param _condition Požadovaná podmínka
     */
    public setFilterCondition(_condition: string): void {
        throw new NotImplementedException();
    }
    public getFilterCondition(): string {
        throw new NotImplementedException();
    }

    /**
     * Nastaví atributy, ktere chci dočíst a dočte data, pokud přibyl nějaký nový atribut - není podporováno
     * @param _attrs seznam atributu
     */
    public async setAttrsAndLoad(_attrs: string[], _cancellationToken?: CancellationToken): Promise<any> {
        return Promise.resolve(); // Nepodporuje
    }

    /**
     * Smaže dočtené záznamy v datasoursu
     * */
    public clearData(): void {
        this._internalRows = [];
        const dsLoadInfo: IDataSourceLoadInfo = {
            startIndex: 0,
            dataGenerator: this._getIterator.bind(this, 0, 0),
            lastBatch: false,
            isFiltered: false,
            refreshedData: false,
            emptyData: true,
        };
        this.dataLoaded.trigger(this, dsLoadInfo);
    }

    /**
     * Načte data
     * @param _keepSelectedRecords Zda mají být zachovány vybrané záznamy - nepodporováno
     */
    public async loadNewData(_keepSelectedRecords?: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData[]> {
        this._runningDataLoadCTS = new CancellationTokenSource([cancellationToken]);

        this._runningDataLoad = this._documentClient.getDocumentVersions(this._instanceId, this._attributeName, this._getRequestContext(this._runningDataLoadCTS.token))
            .then(async (versions) => {
                this._internalRows = [];
                versions = versions.sort((a, b) => a.Number - b.Number);
                for (const version of versions) {
                    const data: IRowData = { ID: version.VersionDocID }; //je potřeba doplnit ID, které bohužel naše datagridy pevně vyžadují.

                    Object.entries(version).forEach(([key, value]) => {
                        data[key] = value;
                    });

                    this._internalRows.push(data);
                }

                const dsLoadInfo: IDataSourceLoadInfo = {
                    startIndex: 0,
                    dataGenerator: this._getIterator.bind(this, 0, this._internalRows.length),
                    lastBatch: true,
                    isFiltered: false,
                    refreshedData: false,
                    cancellationToken: cancellationToken,
                    emptyData: false,
                };

                this._runningDataLoad = null;

                this.dataLoaded.trigger(this, dsLoadInfo);
                this.loadingEnded.trigger(this, dsLoadInfo);

                return this._internalRows;
            })
            .catch((err) => {
                this._runningDataLoad = null;
                this.loadingEnded.trigger(this, { isFiltered: false, emptyData: true, cancellationToken: cancellationToken });
                // zrušení nahrávání data je v pořádku
                if (!(err instanceof CancelError))
                    this.error.trigger(this, err);
            });

        this.loadingStarted.trigger(this, 0);

        return this._runningDataLoad;
    }

    /**
     * Načte další várku dat - není podporováno
     * @param _startIndex
     * @param _count
     * @param _keepSelectedRecords
     */
    public getNextData(_startIndex?: number, _count?: number, _keepSelectedRecords?: boolean, _cancellationToken?: CancellationToken): Promise<any> {
        return Promise.resolve();
    }

    /**
     * Znovu načte data
     * @param _fetchCount Není podporováno
     * @param _manualRefresh Není podporováno
     */
    public refreshData(_fetchCount?: number, _manualRefresh?: boolean, _cancellationToken?: CancellationToken): Promise<any> {
        return this.loadNewData();
    }

    /**
     * Čeká na dočtení dat
     * @param _ct
     */
    public waitForReload(_ct?: CancellationToken): Promise<void> {
        return this._runningDataLoad != null ? this._runningDataLoad : Promise.resolve();
    }

    public [Symbol.iterator](): Iterator<IRowData> {
        if (!this._internalRows)
            this._internalRows = new Array<IRowData>();

        return this._internalRows[Symbol.iterator]();
    }

    protected *_getIterator(startIndex: number, count?: number): IterableIterator<IRowData> {
        const maxIndex = count ? Math.min(startIndex + count, this._internalRows.length) : this._internalRows.length;
        for (let i = startIndex; i < maxIndex; i++)
            yield this._internalRows[i];
    }

    /**
     * Metoda pro dočtení jednoho určitého záznamu
     * @param _dataKey Datový klíč (většinou ID) k načtení
     * @param _selectLoaded Příznak, zda se dočtené záznamy mají označit
     */
    public async loadSpecificRecord(_dataKey: string, _selectLoaded: boolean = false, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData> {
        throw new NotImplementedException();
    }

    /**
    * Metoda pro dočtení určitých záznamů
    * @param _dataKeys Pole datových klíčů (většinou ID) k načtení
    * @param _selectLoaded Příznak, zda se dočtené záznamy mají označit
    */
    public async loadSpecificRecords(_dataKeys: string[], _selectLoaded: boolean = false, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData[]> {
        throw new NotImplementedException();
    }

    /*
     * Vrací počet zadaných záznamů pro aktuálně nastavený filtr
     */
    public async count(): Promise<IRecordsInfo> {
        return {
            isFiltered: false,
            totalCount: this._internalRows.length,
        };
    }

    public dispose(): void {
        // Prázdná metoda
    }
    //#endregion Methods
}
